import { set } from 'lodash-es';
import { z } from 'zod';

import { FetchDecorator } from '@tdp/common/fetchDecorator';
import { ILogger } from '@tdp/common/telemetry';


export const ApiDecoratorOptionsSchema = z.object({
  baseUrl: z.string().url(),
});

export type ApiDecoratorOptions = z.infer<typeof ApiDecoratorOptionsSchema>;
export interface ApiDecoratorDependencies {
  logger: ILogger;
}

/**
 * Create a `fetch` decorator that auto-populates the Authorization header with a bearer token
 * for a given
 * @param tokenProvider the provider for the bearer tokens
 * @param config the options
 */
export function apiDecorator(
  dependencies: ApiDecoratorDependencies,
  config: z.infer<typeof ApiDecoratorOptionsSchema>
): FetchDecorator {
  const { logger } = dependencies;
  function isInvalidResource(input?: Parameters<typeof fetch>[0]) {
    if (input instanceof URL) {
      // if we have a full url, ensure it is for the correct base url.
      return !input.toString().startsWith(config.baseUrl);
    } else if (typeof input === 'string') {
      return !input.startsWith('/') && !input.startsWith(config.baseUrl);
    } else {
      return !input?.url.toString().startsWith(config.baseUrl);
    }
    // invalid input type, should be impossible but if there is a place for strange inputs, it is fetch.
    logger.error('Invalid type for input %s', typeof input);
    return true;
  }

  return function (decorated: typeof fetch): typeof fetch {
    return async function (input, init) {
      init = set(
        init ?? {},
        ['headers', 'Client-Source'],
        'vNextM365DeveloperPortal'
      );

      // if the input
      if (isInvalidResource(input)) {
        logger.error('Attempt to fetch from wrong location');
        // Generally bad practice, but this is almost certainly developer error.
        throw new Error('Attempt to fetch from incorrect location');
      }
      if (typeof input === 'string' && input.startsWith('/')) {
        // this is the normal usage, we want root-relative api paths
        input = `${config.baseUrl}${input}`;
      }
      return await decorated(input, init);
    };
  };
}
